import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { filter } from 'rxjs/operators';

import { MockAuthService } from '@core/auth/mock-auth.service';
import { RoleNavService } from '@core/auth/role-nav.service';
import { BP_DESKTOP } from '@core/constants/breakpoints';
import { RoleKind, RoleKindLabel } from '@core/models/brijjsports.enums';
import { ConfirmService } from '@core/services/confirm.service';
import { SportSwitcherComponent } from '@core/sports/ui/sport-switcher/sport-switcher.component';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';

import { BottomNavComponent } from '../bottom-nav/bottom-nav.component';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { BrijjSportsWordmarkComponent } from '../brijjsports-wordmark/brijjsports-wordmark.component';
import { CommandPaletteComponent } from '../command-palette/command-palette.component';
import { IconComponent } from '../icon/icon.component';
import { NotificationsBellComponent } from '../notifications-bell/notifications-bell.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    BrijjSportsWordmarkComponent,
    IconComponent,
    NotificationsBellComponent,
    SportSwitcherComponent,
    BottomNavComponent,
    BreadcrumbsComponent
  ],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onGlobalKey($event)'
  }
})
export class AppLayoutComponent implements OnInit {
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly confirm = inject(ConfirmService);
  private readonly dialog = inject(MatDialog);
  private paletteOpen = false;

  protected readonly state = inject(BrijjSportsStateService);
  protected readonly nav = inject(RoleNavService);
  protected readonly tenants = inject(TenantContextService);
  protected readonly auth = inject(MockAuthService);

  readonly sidenav = viewChild<MatSidenav>('sidenav');

  readonly isDesktop = signal(false);

  readonly subjectName = computed(() => {
    if (this.tenants.isRoot()) return this.tenants.currentTenant().theme.displayName;
    const role = this.state.currentRole();
    if (role === RoleKind.Owner) return this.state.academy()?.name ?? 'Brijj Sports';
    if (role === RoleKind.Coach) return this.state.currentCoach()?.name ?? '';
    if (role === RoleKind.Parent) return this.state.currentParent()?.name ?? '';
    if (role === RoleKind.Athlete) return this.state.currentAthlete()?.name ?? '';
    return '';
  });

  readonly subjectInitials = computed(() => {
    if (this.tenants.isRoot()) return 'BA';
    const role = this.state.currentRole();
    if (role === RoleKind.Owner) {
      const name = this.state.academy()?.name ?? 'BS';
      return name
        .split(/\s+/)
        .map(p => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    }
    if (role === RoleKind.Coach) return this.state.currentCoach()?.initials ?? '';
    if (role === RoleKind.Parent) return this.state.currentParent()?.initials ?? '';
    if (role === RoleKind.Athlete) return this.state.currentAthlete()?.initials ?? '';
    return '';
  });

  readonly subjectKindLabel = computed(() =>
    this.tenants.isRoot() ? 'Platform admin' : RoleKindLabel[this.state.currentRole()]
  );

  /** Tenant display name shown in the topbar chip. */
  readonly tenantDisplayName = computed(() => this.tenants.currentTenant().theme.displayName);

  /**
   * Per-role profile route used by the "My profile" menu item.
   * Returns null when the active role has no equivalent destination
   * (root admin, parent — both hide the menu item).
   */
  readonly profileRoute = computed<string | null>(() => {
    if (this.tenants.isRoot()) return null;
    switch (this.state.currentRole()) {
      case RoleKind.Owner:
        return '/owner/settings';
      case RoleKind.Coach:
        return '/coach/profile';
      case RoleKind.Athlete:
        return '/athlete/profile';
      default:
        return null;
    }
  });

  ngOnInit(): void {
    this.bpObserver
      .observe(BP_DESKTOP)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(r => this.isDesktop.set(r.matches));

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.isDesktop()) this.sidenav()?.close();
      });
  }

  resetDemo(): void {
    this.confirm
      .ask({
        title: 'Reset the demo?',
        message: 'This wipes all academy data.',
        confirmLabel: 'Reset',
        variant: 'danger'
      })
      .then(ok => {
        if (!ok) return;
        this.state.resetAllTenants();
        this.auth.signOut();
        this.auth.invalidate();
        this.router.navigateByUrl('/welcome');
      });
  }

  signOut(): void {
    this.auth.signOut();
    this.router.navigateByUrl('/login');
  }

  onGlobalKey(event: KeyboardEvent): void {
    if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
    const target = event.target as HTMLElement | null;
    if (isEditableTarget(target) && !this.paletteOpen) return;
    event.preventDefault();
    if (this.paletteOpen) return;
    this.paletteOpen = true;
    this.dialog
      .open(CommandPaletteComponent, {
        panelClass: 'app-command-palette__panel',
        width: '32rem',
        autoFocus: 'first-tabbable',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe(() => {
        this.paletteOpen = false;
      });
  }
}

function isEditableTarget(el: HTMLElement | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}
